"use client";

import { useRouter } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";

import { ApiError, api, loginUrl } from "@/lib/api/client";
import type { Connection, Project } from "@/lib/api/types";

const STORAGE_KEY = "geo.projectId";
export const REFRESH_MS = 60_000;

type Status = "loading" | "ready" | "error";

export type ShellValue = {
  status: Status;
  error: string | null;
  retry: () => void;
  projects: Project[];
  project: Project | null;
  selectProject: (id: number) => void;
  updateProject: (project: Project) => void;
  /** Verbindungen des aktuellen Projekts. `null`, solange sie noch nicht geladen sind. */
  connections: Connection[] | null;
  connectionsFailed: boolean;
  /** Sofort neu laden (z. B. nach einer Aktion). */
  refreshConnections: () => void;
  /** Eine bereits vom Server erhaltene Verbindung übernehmen, ohne neu zu laden. */
  setConnection: (connection: Connection) => void;
};

const ShellContext = createContext<ShellValue | null>(null);
export { ShellContext };

export function useShell(): ShellValue {
  const value = useContext(ShellContext);
  if (!value) throw new Error("useShell braucht einen ShellProvider");
  return value;
}

/** Login-Adresse mit der aktuellen Seite als Ziel nach der Anmeldung. */
function currentLoginUrl(): string {
  return loginUrl(window.location.pathname + window.location.search);
}

function readSavedProject(): number | null {
  try {
    const saved = Number(window.localStorage.getItem(STORAGE_KEY));
    return Number.isInteger(saved) && saved > 0 ? saved : null;
  } catch {
    return null; // localStorage kann gesperrt sein (privates Fenster)
  }
}

/**
 * Gemeinsamer Zustand der App: Projekte, aktuelles Projekt und der Status der Datenquellen.
 * Leitet bei fehlender Anmeldung (401) zur Login-Seite weiter. Die Zugriffskontrolle selbst liegt in der API.
 */
export function ShellProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [connections, setConnections] = useState<Connection[] | null>(null);
  const [connectionsFailed, setConnectionsFailed] = useState(false);
  const [tick, setTick] = useState(0);

  // Projekte laden (zugleich die Anmeldeprüfung: ohne Session antwortet die API mit 401)
  useEffect(() => {
    let cancelled = false;
    api<Project[]>("/api/projects")
      .then((list) => {
        if (cancelled) return;
        const saved = readSavedProject();
        setProjects(list);
        setProjectId(list.find((p) => p.id === saved)?.id ?? list[0]?.id ?? null);
        setError(null);
        setStatus("ready");
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 401) {
          router.replace(currentLoginUrl());
          return;
        }
        setError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [attempt, router]);

  // Datenquellen des aktuellen Projekts laden: beim Wechsel und bei jedem Auffrischen
  useEffect(() => {
    if (projectId === null) return;
    let cancelled = false;
    api<Connection[]>(`/api/connections?project_id=${projectId}`)
      .then((list) => {
        if (cancelled) return;
        setConnections(list);
        setConnectionsFailed(false);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 401) {
          router.replace(currentLoginUrl());
          return;
        }
        setConnectionsFailed(true); // alte Anzeige bleibt stehen, die Sidebar weist auf den Fehler hin
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, tick, router]);

  // „Live“: regelmäßig und beim Zurückkehren in den Tab auffrischen
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") setTick((t) => t + 1);
    };
    const timer = window.setInterval(refresh, REFRESH_MS);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  const value: ShellValue = {
    status,
    error,
    retry: () => {
      setStatus("loading");
      setAttempt((a) => a + 1);
    },
    projects,
    project: projects.find((p) => p.id === projectId) ?? null,
    selectProject: (id) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(id));
      } catch {
        // ohne Speicher gilt die Auswahl nur für diese Sitzung
      }
      setConnections(null);
      setConnectionsFailed(false);
      setProjectId(id);
    },
    updateProject: (next) => setProjects((list) => list.map((p) => (p.id === next.id ? next : p))),
    connections,
    connectionsFailed,
    refreshConnections: () => setTick((t) => t + 1),
    setConnection: (next) =>
      setConnections((list) => list && list.map((c) => (c.source === next.source ? next : c))),
  };

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

/** Zeigt Ladezustand und Fehler, solange Projekte fehlen. Seiten dürfen danach ein Projekt voraussetzen. */
export function ShellGate({ children }: { children: React.ReactNode }) {
  const shell = useShell();

  if (shell.status === "loading") {
    return <p className="p-8 text-sm text-muted">Wird geladen …</p>;
  }
  if (shell.status === "error") {
    return (
      <div
        role="alert"
        className="m-6 flex flex-wrap items-center gap-3 rounded-lg bg-danger-bg px-[18px] py-3.5 text-sm text-danger"
      >
        <span>{shell.error}</span>
        <button
          type="button"
          onClick={shell.retry}
          className="h-11 rounded-md border border-danger bg-white px-3.5 text-sm text-danger"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }
  if (!shell.project) {
    return <p className="p-8 text-sm text-muted">Es gibt noch kein Projekt.</p>;
  }
  return <>{children}</>;
}
